<script>
/**
 * Tarefas pendentes do cliente em foco.
 *
 * Componente BURRO: recebe os GRUPOS como o CRM os devolve e prioriza com o util
 * do módulo. Não chama API — quem busca é a view.
 *
 * ⚠️ A lista aqui é curta de propósito (`MAX_TAREFAS`). A lateral divide a
 * altura com carteira, contatos e o histórico de conversas; uma lista completa
 * empurraria todo o resto para fora da dobra. Quem quer a lista inteira clica no
 * rótulo e vai para o CRM.
 */
import {
  priorizarTarefas,
  STATUS_CRITICA,
  STATUS_ATRASADA,
} from '@modules/aawz-intelligence-v2/utils/cliente-em-foco/prioridade-de-tarefa'
import { contadorDeLista } from '@modules/aawz-intelligence-v2/utils/cliente-em-foco/contador-de-lista'
import { TONS, tokenDoTom } from '@modules/aawz-intelligence-v2/components/ia-insight-card/tons'
import { formatDate } from '@utils/Format'

const ROTULO = 'Tarefas pendentes'
const TAMANHO_CHEVRON = 12
// Duas (era 3 até 14/09/2026), e não as cinco do card do perfil: lá a seção tem
// a página inteira; aqui ela divide a coluna com o resto do painel, e o painel
// inteiro tem de caber no piso de 660px do bloco superior — a conta está no
// `<style>` do `ia-cliente-painel.vue`. O que fica de fora vai para o contador.
const MAX_TAREFAS = 2
// UMA linha. O esqueleto sugere a forma, não promete a quantidade — e cada
// linha a mais é altura que a coluna perde quando o dado chega com menos itens
// do que o esqueleto desenhou.
const LINHAS_PENDENTES = [{ chave: 'p1' }]
const CAIXA_TITULO = { width: '78%', height: '13px' }
const CAIXA_PRAZO = { width: '46%', height: '11px' }

// Tom do anel de cada tarefa. Crítica e atrasada pedem ação hoje; o resto é
// agenda, e pintar tudo de vermelho apaga a diferença entre as duas coisas.
const TOM_POR_STATUS = {
  [STATUS_CRITICA]: TONS.CRITICO,
  [STATUS_ATRASADA]: TONS.CRITICO,
}

export default {
  name: 'IaClienteTarefas',
  props: {
    // `response.value.grupos` do CRM, cru.
    grupos: { type: Array, default: () => [] },
    carregando: { type: Boolean, default: false },
    navegavel: { type: Boolean, default: false },
  },
  data() {
    return { LINHAS_PENDENTES, CAIXA_TITULO, CAIXA_PRAZO, TAMANHO_CHEVRON }
  },
  computed: {
    rotulo() {
      return ROTULO
    },
    priorizadas() {
      return priorizarTarefas(this.grupos, { maximo: MAX_TAREFAS })
    },
    tarefas() {
      return this.priorizadas.tarefas.map((tarefa, indice) => ({
        chave: tarefa.titulo + ':' + indice,
        titulo: tarefa.titulo,
        prazo: this.textoDoPrazo(tarefa),
        corDoAnel: tokenDoTom(TOM_POR_STATUS[tarefa.status] || TONS.NEUTRO),
        alerta: Boolean(TOM_POR_STATUS[tarefa.status]),
      }))
    },
    // "2 de 3+" — o que aparece contra o que existe (o util só sabe que há mais
    // além do teto, não quantos). Fórmula única em `contadorDeLista`.
    contador() {
      return contadorDeLista({
        exibidos: this.priorizadas.tarefas.length,
        temMais: this.priorizadas.temMais,
      })
    },
    vazio() {
      return !this.carregando && !this.tarefas.length
    },
  },
  methods: {
    // Prazo + o status quando ele exige ação: "Atrasado · 12/08". Só a data
    // esconderia que a tarefa venceu.
    textoDoPrazo(tarefa) {
      const data = tarefa.prazo ? formatDate(tarefa.prazo, 'Curto') : ''
      if (tarefa.status === STATUS_CRITICA) return ['Crítica', data].filter(Boolean).join(' · ')
      if (tarefa.status === STATUS_ATRASADA) return ['Atrasada', data].filter(Boolean).join(' · ')
      return data
    },
    aoClicar() {
      if (!this.navegavel) return
      this.$emit('abrir')
    },
  },
}
</script>

<template>
  <div class="ia-cliente-tarefas">
    <component
      :is="navegavel ? 'button' : 'div'"
      class="ia-cliente-tarefas__rotulo ia-texto-selo"
      :type="navegavel ? 'button' : null"
      @click="aoClicar"
    >
      <span>{{ rotulo }}</span>
      <!-- ⚠️ `arrow-left` aponta para a DIREITA — ver a nota no `ia-insight-card`. -->
      <icon-svg
        v-if="navegavel"
        name="arrow-left"
        :size="TAMANHO_CHEVRON"
        color="var(--neutral-600)"
      />
      <span v-if="!carregando" class="ia-sidebar-contador ml-auto" data-test="ia-cliente-tarefas-contador">
        {{ contador }}
      </span>
    </component>

    <!-- Carregando é a própria lista com as linhas pendentes, não um bloco
         cinza: a forma final já é conhecida. Mesma escolha do `ia-insight-card`. -->
    <ul v-if="carregando" class="ia-cliente-tarefas__lista">
      <li v-for="linha in LINHAS_PENDENTES" :key="linha.chave">
        <span class="ia-cliente-tarefas__anel" />
        <span class="ia-cliente-tarefas__texto">
          <BaseLoadingSkeleton v-bind="CAIXA_TITULO" />
          <BaseLoadingSkeleton v-bind="CAIXA_PRAZO" />
        </span>
      </li>
    </ul>

    <p v-else-if="vazio" class="ia-cliente-tarefas__vazio ia-texto-auxiliar">
      Nenhuma tarefa pendente
    </p>

    <ul v-else class="ia-cliente-tarefas__lista">
      <li v-for="tarefa in tarefas" :key="tarefa.chave">
        <span
          class="ia-cliente-tarefas__anel"
          :style="{ borderColor: tarefa.corDoAnel }"
        />
        <span class="ia-cliente-tarefas__texto">
          <span class="ia-cliente-tarefas__titulo ia-texto-corpo">{{ tarefa.titulo }}</span>
          <span
            v-if="tarefa.prazo"
            class="ia-cliente-tarefas__prazo ia-texto-auxiliar"
            :style="tarefa.alerta ? { color: tarefa.corDoAnel } : null"
          >
            {{ tarefa.prazo }}
          </span>
        </span>
      </li>
    </ul>
  </div>
</template>

<style lang="sass" scoped>
.ia-cliente-tarefas
  display: flex
  flex-direction: column
  gap: 10px

// Mesma régua do `.ia-insight__rotulo` — os blocos abrem igual na coluna.
.ia-cliente-tarefas__rotulo
  color: var(--neutral-600)
  display: flex
  align-items: center
  gap: 4px
  width: 100%
  padding: 0
  background: transparent
  border: 0
  letter-spacing: 0.06em
  text-transform: uppercase

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

.ia-cliente-tarefas__lista
  display: flex
  flex-direction: column
  gap: 10px
  margin: 0
  padding: 0
  list-style: none

  li
    display: flex
    align-items: flex-start
    gap: 10px

// Anel vazado, não bola cheia: a tarefa está PENDENTE, e um disco sólido lê
// como concluída. A cor mora na borda pelo mesmo motivo.
.ia-cliente-tarefas__anel
  flex-shrink: 0
  width: 12px
  height: 12px
  margin-top: 2px
  border: 1.5px solid var(--neutral-300)
  border-radius: 50%

.ia-cliente-tarefas__texto
  display: flex
  flex-direction: column
  gap: 2px
  // ⚠️ `flex: 1` é o que faz o esqueleto aparecer: as caixas dele têm largura em
  // PORCENTAGEM, e sem base de largura aqui elas resolviam contra zero — a
  // linha ficava só com a bolinha, como se o texto tivesse sumido.
  flex: 1
  min-width: 0

.ia-cliente-tarefas__titulo
  color: var(--neutral-900)
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap

.ia-cliente-tarefas__vazio
  color: var(--neutral-600)
  margin: 0

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.ia-cliente-tarefas__prazo
  color: var(--neutral-600)
</style>
